'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { Headline } from 'components/ContentElementsGdds/headline/headline';
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';
import { InnerHtml } from 'components/InnerHtml/inner-html';
import { MiVideoContainer } from 'components/Video/miVideo/MiVideoContainer';
import ConfigurableYoutube from 'components/ContentElementsGdds/video-channel/configurable-youtube';
import SocialShare from 'components/SocialShare/SocialShare';
import { calculateVideoPlayerDimensions } from './media-overlay.utils';
import styles from './media-overlay.module.scss';
import type { MediaItem } from './types';

type VideoContentProps = {
  mediaItem: MediaItem;
};

export function VideoContent({ mediaItem }: Readonly<VideoContentProps>) {
  const [maxWidth, setMaxWidth] = useState<number>(0);

  const videoData = (mediaItem as any)?.video;
  const title = (mediaItem as any)?.title;
  const description = (mediaItem as any)?.description;

  const customStyles = { '--max-width': `${maxWidth}px` } as React.CSSProperties;

  const socialShare = {
    title: title || '',
    url: typeof window !== 'undefined' ? window.location.href : '',
    image: '',
  };

  const updateDimensions = useCallback(() => {
    const { maxWidth: newMaxWidth } = calculateVideoPlayerDimensions();
    setMaxWidth(newMaxWidth);
  }, []);

  useEffect(() => {
    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [updateDimensions]);

  return (
    <div className={styles.videoWrapper} style={customStyles}>
      {title && (
        <div className={styles.videoTitle}>
          <Headline tag={Formats.h3} title={title} />
        </div>
      )}

      <div className={styles.videoContent}>
        {videoData.type === 'youtube' ? (
          <ConfigurableYoutube
            videoId={videoData.videoId}
            playerControl={videoData.playerControl}
            language={videoData.language}
            showLink={videoData.showLink}
          />
        ) : (
          <MiVideoContainer
            className="c-text-video"
            content={{
              video: videoData,
              autoplay: false,
              buttonText: '',
              videoVariant: 'buttonOnly',
            }}
          />
        )}
      </div>

      {description && (
        <div className={styles.videoDescription}>
          <InnerHtml as="div" content={description} stylingType="inverted" isGdds />
        </div>
      )}

      <SocialShare metaData={socialShare} contentType="video" isGddsLightbox />
    </div>
  );
}
